import { CommonModule } from '@angular/common';
import {
  Component,
  viewChild,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonsModule,
  CardModule,
  CheckboxModule,
  IconModule,
  LoadingService,
} from 'axis-components';
import { FileUploadModule } from 'axis-components/file-upload';
import { MediaModule } from 'axis-components/media';
import { TabsModule } from 'axis-components/tab';
import { PdfService } from '../../../_services/pdf/pdf.service';
import { SignatureService } from '../../../_services/pip/signature.service';
import { RequestService } from '../../../_services/request/request.service';
import { UserService } from '../../../_services/user.service';
import { AnalyticsService } from '../../../_shared/analytics/analytics.service';
import { SignatureComponent } from '../../../_shared/signature/signature.component';
import {
  FormType,
  PDFGenerationStatus,
  RequestProgressStep,
  UserRole,
} from '../../../config/constants';
import { PipBaseRulesService } from '../../../state-disclosures/pip/services/pip-base-rules.service';
import { BrowserNavigationService } from '../../../_services/browser-navigation.service';
import { PipBaseStateComponent } from '../../../state-disclosures/pip/pip-base-state.component';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-pip-signature',
  standalone: true,
  imports: [
    TabsModule,
    CardModule,
    CommonModule,
    FileUploadModule,
    ButtonsModule,
    CheckboxModule,
    MediaModule,
    IconModule,
    ReactiveFormsModule,
    SignatureComponent,
  ],
  templateUrl: './pip-signature.component.html',
  styleUrls: ['./pip-signature.component.scss'],
})
export class PipSignatureComponent {
  // Events emitted to playground component for workflow navigation
  @Output() continueClickedPlayground = new EventEmitter<void>();
  @Output() backClickedPlayground = new EventEmitter<void>();
  @Input() ctaData: any = {};

  public pipSignature: FormGroup;
  signatureData: any;
  currentDateTime = new Date();
  dateStr: string;
  signaturePad = viewChild<SignatureComponent>('signaturePad');
  showSignatureError: boolean = false;
  role;
  accountDetails: any = null;
  flow: string = '';

  constructor(
    private readonly signatureService: SignatureService,
    public userService: UserService,
    private router: Router,
    private baseRulesService: PipBaseRulesService,
    private fb: FormBuilder,
    private pdfService: PdfService,
    private requestService: RequestService,
    private analyticsService: AnalyticsService,
    public route: ActivatedRoute,
    private browserNavigationService: BrowserNavigationService,
    private loadingService: LoadingService,
  ) {}

  ngOnInit(): void {
    this.browserNavigationService.startListening({
      onBackNavigation: () => this.onBrowserback(),
    });
    this.role = this.userService.role;
    this.getAccountDetails();
    const nameAndTitle = this.accountDetails
      ? `${this.accountDetails.insuredName || ''} ${this.accountDetails.title || ''}`.trim()
      : '';
    if (this.role === UserRole.BROKER) {
      this.pipSignature = this.fb.group({
        pipSignCheck: [''],
        pipInsuredName: [{ value: '', disabled: true }],
      });
    } else if (this.role === UserRole.INSURED) {
      this.pipSignature = this.fb.group({
        pipSignCheck: ['', Validators.required],
        pipInsuredName: [
          { value: nameAndTitle, disabled: false },
          [Validators.required, Validators.pattern(/^[^\d]+$/)],
        ],
      });
    }

    this.signatureService.getSignatureData().subscribe((data) => {
      this.signatureData = data;
    });
    this.goTop();

    this.route.queryParams.subscribe((params) => {
      if (params['flow']) {
        setTimeout(() => {
          this.flow = params['flow'];
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    this.browserNavigationService.stopListening();
  }

  onBrowserback(): void {
    this.updateonback();
  }

  updateonback(): void {
    this.baseRulesService.pdfDataDump = {
      ...this.baseRulesService.pdfSummaryDataDump,
    };
    this.baseRulesService.isContinueButtonClicked = false;
  }
  getAccountDetails(): void {
    if (
      this.requestService.request &&
      this.requestService.request.requestDetails &&
      this.requestService.request.requestDetails.accountDetail
    ) {
      this.accountDetails =
        this.requestService.request.requestDetails.accountDetail;
    }
  }
  onSignatureChange(): void {
    this.showSignatureError = false;
  }
  goTop(): void {
    // reset the page when changing route
    window.scrollTo(0, 0);
  }
  onBack(): void {
    if (this.baseRulesService.isPlayground) {
      // In playground mode, emit event instead of navigating
      this.backClickedPlayground.emit();
      return;
    }

    this.updateonback();
    this.router.navigate(['/insured/pip-outstanding-state'], {
      queryParamsHandling: 'preserve',
    });
  }
  onContinue(): void {
    let isValid = false;
    const signatureData = this.signaturePad().signatureData;
    if (!signatureData?.data || signatureData.data.trim() === '') {
      this.showSignatureError = true;
      isValid = true;
    } else {
      this.showSignatureError = false;
    }
    if (this.pipSignature?.invalid) {
      Object.keys(this.pipSignature?.controls).forEach((key) => {
        this.pipSignature.get(key)?.markAsTouched();
      });
      isValid = true;
    }
    if (isValid) {
      window.scrollTo(0, 0);
      return;
    }
    const reqEffectiveDate =
      this.requestService.request?.requestDetails?.policyDetail?.[0]
        ?.effectiveDate;
    // Format the effective date from MM-DD-YYYY to MM/DD/YYYY
    const formattedEffectiveDate = reqEffectiveDate
      ? reqEffectiveDate.replace(/-/g, '/')
      : '';
    const signatureObj = {
      nameTitle: this.pipSignature.value['pipInsuredName'],
      method: this.signaturePad().signatureData.method,
      data: this.signaturePad().signatureData.data,
      effectiveDate: formattedEffectiveDate,
      dateSigned: this.formatDate(new Date().toISOString()),
    };

    this.pdfService.signatureObject = signatureObj;

    if (this.baseRulesService.isPlayground) {
      // In playground mode, emit event instead of navigating
      this.continueClickedPlayground.emit();
      return;
    }

    const submitWithCompletionGuard = (wasAlreadyCompleted: boolean) => {
      const isPipCompleted = this.requestService.isFormTypeAlreadyCompleted(
        FormType.PIP,
      );

      // If PIP is already completed, do not continue with consent routing.
      if (isPipCompleted) {
        return;
      }
      PipBaseStateComponent.resetLoadingState();
      this.loadingService.start('Loading');
      this.requestService
        .updateRequestProgress(
          FormType.PIP,
          RequestProgressStep.ELECTRONIC_SIGNATURE,
          this.baseRulesService.dataDump,
        )
        .then(() => {
          this.pdfService.pipPdfGenerationStatus =
            PDFGenerationStatus.RequestSent;
          this.pdfService
            .generatePIPPdfAndSendEmail()
            .pipe(
              finalize(() => {
                this.loadingService.stop();
                if (!this.userService.role.match('broker')) {
                  // Navigate to thank you page after step update is complete
                  this.router.navigate(['/thank-you'], {
                    queryParamsHandling: 'preserve',
                  });
                }
              }),
            )
            .subscribe(
              (res) => {
                this.pdfService.pipPdfGenerationStatus =
                  PDFGenerationStatus.ResponseReceived;
              },
              (err) => {
                console.error('Error sending Email with PDF:', err);
                this.pdfService.pipPdfGenerationStatus =
                  PDFGenerationStatus.Error;
              },
            );

          this.analyticsService.logPIPSelectionOfCoverages();

          // Get current origin for dynamic URL construction
          const baseUrl = window.location.origin;
          const targetRoute = '/thank-you';

          // Only emit completion when this submit newly completes the PIP form.
          if (!wasAlreadyCompleted) {
            this.analyticsService.emitFormCompleteOnce(
              FormType.PIP,
              'DigiSign:pip:thankYou',
              targetRoute,
            );
          }

          // Setup CTA data for completion tracking
          this.ctaData.ctaName = 'DigiSign:pip:signature:continue';
          this.ctaData.ctaUrl = `${baseUrl}${targetRoute}`;
          this.analyticsService.setAdobeDataLayerCTAEvent(this.ctaData);
        })
        .catch((err) => {
          console.error('Error updating PIP step:', err);
          // Navigate anyway to avoid blocking user
          if (!this.userService.role.match('broker')) {
            this.router.navigate(['/thank-you'], {
              queryParamsHandling: 'preserve',
            });
          }
        });
    };

    // Refresh from DB before submit to guard duplicate completes across sessions/browsers.
    this.requestService.refreshRequestData(false).subscribe({
      next: () => {
        submitWithCompletionGuard(
          this.requestService.isFormTypeAlreadyCompleted(FormType.PIP),
        );
      },
      error: () => {
        submitWithCompletionGuard(
          this.requestService.isFormTypeAlreadyCompleted(FormType.PIP),
        );
      },
    });
  }
  formatDate(dateString: string): string {
    if (!dateString) return '';

    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);

    const formattedMonth = String(month).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');

    return `${formattedMonth}/${formattedDay}/${year}`;
  }
}
